import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, CalendarCheck, Loader2, CheckCircle2, Clock } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { DOCTOR, whatsappLink } from "@/config/doctorConfig";
import { SectionHeading, Reveal } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["Evening OPD (4:00 – 7:30 PM)", "Morning (on request)", "Afternoon (on request)"];

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-teal-600 focus:outline-none transition-colors duration-200";

export const Appointment = () => {
  const [form, setForm] = useState({ name: "", mobile: "", preferred_date: "", preferred_time: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
      toast.success("Appointment request received", {
        description: "The clinic will confirm your timing shortly. You can also confirm instantly on WhatsApp.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not submit request", {
        description: typeof detail === "string" ? detail : "Please check your details or call the clinic directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  const waConfirm = whatsappLink(
    `Hello Dr. Suyash Bhadoriya, I am ${form.name}. I requested an appointment for ${form.preferred_date} (${form.preferred_time}). Reason: ${form.reason || "General consultation"}. Please confirm.`
  );

  return (
    <section id="appointment" className="bg-background py-16 sm:py-24 lg:py-32" data-testid="appointment-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading
              number="06"
              eyebrow="Appointments"
              title="Book a Consultation with Dr. Suyash Bhadoriya"
              sub="Share your details and preferred time — the clinic will confirm your appointment. For urgent concerns, call directly."
            />
            <Reveal delay={3}>
              <div className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-teal-50 border border-teal-200/70 px-5 py-2.5" data-testid="appointment-opd-timings">
                <Clock size={15} className="text-teal-700" />
                <span className="text-sm font-semibold text-teal-900">
                  OPD: {DOCTOR.opdTime} ({DOCTOR.opdDays}) · {DOCTOR.opdSunday} · {DOCTOR.opdOff}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                <a
                  href={DOCTOR.clinicPhoneHref}
                  data-testid="appointment-call-clinic-btn"
                  className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 shadow-soft p-5 hover:border-teal-500/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
                >
                  <span className="w-11 h-11 rounded-xl bg-navy-900 flex items-center justify-center shrink-0">
                    <Phone size={18} className="text-teal-300" />
                  </span>
                  <span>
                    <span className="block text-xs font-mono uppercase tracking-wider text-slate-500">Call Clinic</span>
                    <span className="block font-serif font-semibold text-navy-900 text-lg">{DOCTOR.clinicPhoneDisplay}</span>
                  </span>
                </a>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="appointment-whatsapp-btn"
                  className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 shadow-soft p-5 hover:border-teal-500/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#128C7E] flex items-center justify-center shrink-0">
                    <WhatsAppIcon size={18} className="text-white" />
                  </span>
                  <span>
                    <span className="block text-xs font-mono uppercase tracking-wider text-slate-500">WhatsApp</span>
                    <span className="block font-serif font-semibold text-navy-900 text-lg">{DOCTOR.mobileDisplay}</span>
                  </span>
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={1}>
              <div className="bg-white rounded-3xl border border-slate-200/80 shadow-lift p-6 sm:p-10">
                {done ? (
                  <div className="text-center py-10" data-testid="appointment-success">
                    <CheckCircle2 size={52} className="mx-auto text-teal-600" />
                    <h3 className="mt-5 font-serif font-bold text-2xl text-slate-900">Request received</h3>
                    <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-md mx-auto leading-relaxed">
                      Thank you, {form.name.split(" ")[0]}. Your appointment request for{" "}
                      <strong>{form.preferred_date}</strong> ({form.preferred_time.toLowerCase()}) has been
                      recorded. The clinic will confirm shortly on {form.mobile}.
                    </p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                      <a
                        href={waConfirm}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="appointment-confirm-whatsapp-btn"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-[#128C7E] hover:bg-[#0d6e63] transition-colors duration-200"
                      >
                        <WhatsAppIcon size={16} /> Confirm on WhatsApp
                      </a>
                      <button
                        onClick={() => { setDone(false); setForm({ name: "", mobile: "", preferred_date: "", preferred_time: "", reason: "" }); }}
                        data-testid="appointment-new-request-btn"
                        className="px-6 py-3 rounded-full text-sm font-semibold text-navy-900 border border-slate-300 hover:border-teal-600 transition-colors duration-200"
                      >
                        New request
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="appointment-form" noValidate={false}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="appt-name" className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Full Name *</label>
                        <input id="appt-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="appointment-name-input" />
                      </div>
                      <div>
                        <label htmlFor="appt-mobile" className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Mobile Number *</label>
                        <input id="appt-mobile" required type="tel" inputMode="tel" pattern="[0-9]{10,15}" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile number" className={inputCls} data-testid="appointment-mobile-input" />
                      </div>
                      <div>
                        <label htmlFor="appt-date" className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Preferred Date *</label>
                        <input id="appt-date" required type="date" min={new Date().toISOString().split("T")[0]} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="appointment-date-input" />
                      </div>
                      <div>
                        <label htmlFor="appt-time" className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Preferred Time *</label>
                        <select id="appt-time" required value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} data-testid="appointment-time-select">
                          <option value="" disabled>Select a time of day</option>
                          {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="appt-reason" className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Reason for Consultation</label>
                        <textarea id="appt-reason" rows={3} maxLength={500} value={form.reason} onChange={set("reason")} placeholder="e.g. Diabetes review, persistent fever, health checkup…" className={`${inputCls} resize-none`} data-testid="appointment-reason-input" />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="appointment-submit-btn"
                      className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl text-sm font-semibold text-white bg-navy-900 hover:bg-navy-700 disabled:opacity-60 shadow-soft transition-colors duration-200"
                    >
                      {loading ? <Loader2 size={17} className="animate-spin" /> : <CalendarCheck size={17} />}
                      {loading ? "Submitting…" : "Request Appointment"}
                    </button>
                    <p className="mt-4 text-xs text-slate-500 leading-relaxed text-center">
                      Your details are used only to arrange your consultation and are not shared publicly.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
